import { Card, CardHeader, CardTitle, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { HTMLEditor } from '../../components/common/HTMLEditor';
import { useState, useEffect } from 'react';
import apiService from '../../services/api';
import { 
  EnvelopeIcon,
  CogIcon,
  ExclamationTriangleIcon,
  ServerIcon,
  EyeIcon,
  EyeSlashIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface ReminderConfig {
  id?: number;
  sender_name: string;
  sender_email: string;
  reply_to_email: string;
  smtp_host: string;
  smtp_port: number;
  smtp_secure: boolean;
  smtp_user: string;
  smtp_password: string;
  first_reminder_days: number;
  second_reminder_days: number;
  final_reminder_days: number;
  legal_reminder_days: number;
  enable_legal_reminder: boolean;
  first_reminder_subject: string;
  first_reminder_template: string;
  second_reminder_subject: string;
  second_reminder_template: string;
  final_reminder_subject: string;
  final_reminder_template: string;
  legal_reminder_subject: string;
  legal_reminder_template: string;
  is_active: boolean;
}

export const ReminderConfig = () => {
  const [config, setConfig] = useState<ReminderConfig>({
    sender_name: 'NodeCrafts',
    sender_email: 'faktury@nodecrafts.pl',
    reply_to_email: 'kontakt@nodecrafts.pl',
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_secure: false,
    smtp_user: '',
    smtp_password: '',
    first_reminder_days: 7,
    second_reminder_days: 14,
    final_reminder_days: 7,
    legal_reminder_days: 14,
    enable_legal_reminder: false,
    first_reminder_subject: 'Przypomnienie o płatności - Faktura {{invoice_number}}',
    first_reminder_template: `Szanowni Państwo,

Przypominamy o płatności faktury {{invoice_number}} na kwotę {{amount_gross}} wystawionej dnia {{issue_date}}.

Termin płatności: {{due_date}}
Faktura jest zaległa od {{days_overdue}} dni.

Prosimy o uregulowanie należności w możliwie najkrótszym czasie.

Z poważaniem,
{{sender_name}}`,
    second_reminder_subject: 'Drugie przypomnienie - Faktura {{invoice_number}}',
    second_reminder_template: `Szanowni Państwo,

To już drugie przypomnienie o płatności faktury {{invoice_number}} na kwotę {{amount_gross}}.

Faktura jest zaległa od {{days_overdue}} dni. Prosimy o pilne uregulowanie należności.

Z poważaniem,
{{sender_name}}`,
    final_reminder_subject: 'Ostateczne wezwanie do zapłaty - Faktura {{invoice_number}}',
    final_reminder_template: `Szanowni Państwo,

To ostateczne wezwanie do zapłaty faktury {{invoice_number}} na kwotę {{amount_gross}}.

Faktura jest zaległa od {{days_overdue}} dni. W przypadku braku płatności będziemy zmuszeni podjąć kroki prawne.

Z poważaniem,
{{sender_name}}`,
    legal_reminder_subject: 'Przekazanie sprawy do windykacji - Faktura {{invoice_number}}',
    legal_reminder_template: `Szanowni Państwo,

Informujemy, że sprawę faktury {{invoice_number}} na kwotę {{amount_gross}} przekazaliśmy do działu prawnego.

Z poważaniem,
{{sender_name}}`,
    is_active: true
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/payment-reminders/config');
      
      if (response.data) {
        setConfig({
          ...response.data,
          smtp_password: response.data.smtp_password ? '••••••••' : ''
        });
      }
    } catch (err: any) {
      if (err.response?.status !== 404) {
        setError(err.response?.data?.message || 'Failed to fetch configuration');
        console.error('Error fetching config:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Prepare data - don't send password if it's masked
      const saveData = { ...config };
      if (saveData.smtp_password === '••••••••') {
        delete saveData.smtp_password;
      }

      const method = config.id ? 'PUT' : 'POST';
      const url = config.id 
        ? `/payment-reminders/config/${config.id}`
        : '/payment-reminders/config';

      if (method === 'PUT') {
        await apiService.put(url, saveData);
      } else {
        await apiService.post(url, saveData);
      }

      setSuccess('Konfiguracja została zapisana pomyślnie');
      if (!config.id) {
        // Refresh to get the ID after creation
        await fetchConfig();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save configuration');
      console.error('Error saving config:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof ReminderConfig, value: any) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTestEmail = async () => {
    if (!testEmail.trim()) {
      setError('Podaj adres email do testowania');
      return;
    }

    try {
      setTesting(true);
      setError(null);
      setSuccess(null);

      await apiService.post('/payment-reminders/test-email', {
        email: testEmail,
        config: config
      });

      setSuccess(`Wiadomość testowa została wysłana na adres: ${testEmail}`);
      setTestEmail('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Błąd wysyłania wiadomości testowej');
    } finally {
      setTesting(false);
    }
  };

  const smtpPresets = [
    {
      name: 'Gmail',
      host: 'smtp.gmail.com',
      port: 587,
      secure: false
    },
    {
      name: 'Outlook/Hotmail',
      host: 'smtp-mail.outlook.com',
      port: 587,
      secure: false
    },
    {
      name: 'Yahoo',
      host: 'smtp.mail.yahoo.com',
      port: 587,
      secure: false
    },
    {
      name: 'Onet',
      host: 'smtp.poczta.onet.pl',
      port: 465,
      secure: true
    },
    {
      name: 'WP',
      host: 'smtp.wp.pl',
      port: 465,
      secure: true
    }
  ];

  const applyPreset = (preset: typeof smtpPresets[0]) => {
    setConfig(prev => ({
      ...prev,
      smtp_host: preset.host,
      smtp_port: preset.port,
      smtp_secure: preset.secure
    }));
  };

  const availableVariables = [
    '{{invoice_number}}',
    '{{client_name}}',
    '{{amount_gross}}',
    '{{currency}}',
    '{{due_date}}',
    '{{issue_date}}',
    '{{days_overdue}}',
    '{{sender_name}}',
    '{{current_date}}'
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Ładowanie konfiguracji...</span>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Konfiguracja przypomnień o płatności</h1>
        <p className="mt-1 text-sm text-gray-500">
          Skonfiguruj automatyczne przypomnienia o płatnościach zaległych faktur
        </p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Błąd</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <CogIcon className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Sukces</h3>
              <div className="mt-2 text-sm text-green-700">{success}</div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* SMTP Server Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <ServerIcon className="h-5 w-5 mr-2" />
              Konfiguracja serwera SMTP
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Presets */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Szybka konfiguracja
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                {smtpPresets.map((preset) => (
                  <Button
                    key={preset.name}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => applyPreset(preset)}
                    className="text-xs"
                  >
                    {preset.name}
                  </Button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Host SMTP *
                </label>
                <input
                  type="text"
                  value={config.smtp_host}
                  onChange={(e) => handleInputChange('smtp_host', e.target.value)}
                  placeholder="smtp.gmail.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Port SMTP *
                </label>
                <input
                  type="number"
                  value={config.smtp_port}
                  onChange={(e) => handleInputChange('smtp_port', parseInt(e.target.value))}
                  placeholder="587"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="smtp_secure"
                checked={config.smtp_secure}
                onChange={(e) => handleInputChange('smtp_secure', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="smtp_secure" className="ml-2 block text-sm text-gray-900">
                Użyj SSL/TLS (port 465)
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nazwa użytkownika SMTP *
                </label>
                <input
                  type="text"
                  value={config.smtp_user}
                  onChange={(e) => handleInputChange('smtp_user', e.target.value)}
                  placeholder="twoj.email@gmail.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hasło SMTP *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={config.smtp_password}
                    onChange={(e) => handleInputChange('smtp_password', e.target.value)}
                    placeholder={config.smtp_password === '••••••••' ? 'Hasło jest ustawione' : 'Wprowadź hasło'}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-4 w-4 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-4 w-4 text-gray-400" />
                    )}
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Dla Gmail użyj hasła aplikacji, nie hasła do konta
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Email Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <EnvelopeIcon className="h-5 w-5 mr-2" />
              Ustawienia nadawcy
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nazwa nadawcy *
                </label>
                <input
                  type="text"
                  value={config.sender_name}
                  onChange={(e) => handleInputChange('sender_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email nadawcy *
                </label>
                <input
                  type="email"
                  value={config.sender_email}
                  onChange={(e) => handleInputChange('sender_email', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email do odpowiedzi *
              </label>
              <input
                type="email"
                value={config.reply_to_email}
                onChange={(e) => handleInputChange('reply_to_email', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Adres, na który będą kierowane odpowiedzi na przypomnienia
              </p>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={config.is_active}
                onChange={(e) => handleInputChange('is_active', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                Aktywna konfiguracja (włącz wysyłanie przypomnień)
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Test Email */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 mr-2" />
              Test konfiguracji email
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adres email do testowania
              </label>
              <div className="flex space-x-3">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@example.com"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                <Button
                  type="button"
                  onClick={handleTestEmail}
                  disabled={testing || !testEmail.trim()}
                  variant="outline"
                >
                  {testing ? 'Wysyłanie...' : 'Wyślij test'}
                </Button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Wyślij przykładowe przypomnienie, aby sprawdzić poprawność konfiguracji SMTP
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Timing Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Harmonogram przypomnień</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pierwsze przypomnienie (dni po terminie)
                </label>
                <input
                  type="number"
                  min="1"
                  value={config.first_reminder_days}
                  onChange={(e) => handleInputChange('first_reminder_days', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Drugie przypomnienie (dni po pierwszym)
                </label>
                <input
                  type="number"
                  min="1"
                  value={config.second_reminder_days}
                  onChange={(e) => handleInputChange('second_reminder_days', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ostateczne wezwanie (dni po drugim)
                </label>
                <input
                  type="number"
                  min="1"
                  value={config.final_reminder_days}
                  onChange={(e) => handleInputChange('final_reminder_days', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Przekazanie do prawnika (dni po ostatecznym)
                </label>
                <input
                  type="number"
                  min="1"
                  value={config.legal_reminder_days}
                  onChange={(e) => handleInputChange('legal_reminder_days', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={!config.enable_legal_reminder}
                />
              </div>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="enable_legal"
                checked={config.enable_legal_reminder}
                onChange={(e) => handleInputChange('enable_legal_reminder', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="enable_legal" className="ml-2 block text-sm text-gray-900">
                Włącz przypomnienia prawne
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Available Variables */}
        <Card>
          <CardHeader>
            <CardTitle>Dostępne zmienne</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-gray-50 p-4 rounded-md">
              <p className="text-sm text-gray-600 mb-2">
                Możesz używać następujących zmiennych w tematach i treściach emaili:
              </p>
              <div className="flex flex-wrap gap-2">
                {availableVariables.map((variable) => (
                  <span
                    key={variable}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {variable}
                  </span>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* First Reminder Template */}
        <Card>
          <CardHeader>
            <CardTitle>Pierwsze przypomnienie</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Temat wiadomości
              </label>
              <input
                type="text"
                value={config.first_reminder_subject}
                onChange={(e) => handleInputChange('first_reminder_subject', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Treść wiadomości (HTML)
              </label>
              <HTMLEditor
                value={config.first_reminder_template}
                onChange={(value) => handleInputChange('first_reminder_template', value)}
                placeholder="Wprowadź treść pierwszego przypomnienia..."
                height="300px"
              />
              <p className="text-sm text-gray-500 mt-2">
                Dostępne zmienne: {'{'}{'{'} invoice_number{'}'}{'}'}, {'{'}{'{'} client_name{'}'}{'}'}, {'{'}{'{'} amount_gross{'}'}{'}'}, {'{'}{'{'} due_date{'}'}{'}'}, {'{'}{'{'} days_overdue{'}'}{'}'}, {'{'}{'{'} sender_name{'}'}{'}'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Second Reminder Template */}
        <Card>
          <CardHeader>
            <CardTitle>Drugie przypomnienie</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Temat wiadomości
              </label>
              <input
                type="text"
                value={config.second_reminder_subject}
                onChange={(e) => handleInputChange('second_reminder_subject', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Treść wiadomości (HTML)
              </label>
              <HTMLEditor
                value={config.second_reminder_template}
                onChange={(value) => handleInputChange('second_reminder_template', value)}
                placeholder="Wprowadź treść drugiego przypomnienia..."
                height="300px"
              />
              <p className="text-sm text-gray-500 mt-2">
                Dostępne zmienne: {'{'}{'{'} invoice_number{'}'}{'}'}, {'{'}{'{'} client_name{'}'}{'}'}, {'{'}{'{'} amount_gross{'}'}{'}'}, {'{'}{'{'} due_date{'}'}{'}'}, {'{'}{'{'} days_overdue{'}'}{'}'}, {'{'}{'{'} sender_name{'}'}{'}'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Final Reminder Template */}
        <Card>
          <CardHeader>
            <CardTitle>Ostateczne wezwanie</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Temat wiadomości
              </label>
              <input
                type="text"
                value={config.final_reminder_subject}
                onChange={(e) => handleInputChange('final_reminder_subject', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Treść wiadomości (HTML)
              </label>
              <HTMLEditor
                value={config.final_reminder_template}
                onChange={(value) => handleInputChange('final_reminder_template', value)}
                placeholder="Wprowadź treść ostatecznego wezwania..."
                height="300px"
              />
              <p className="text-sm text-gray-500 mt-2">
                Dostępne zmienne: {'{'}{'{'} invoice_number{'}'}{'}'}, {'{'}{'{'} client_name{'}'}{'}'}, {'{'}{'{'} amount_gross{'}'}{'}'}, {'{'}{'{'} due_date{'}'}{'}'}, {'{'}{'{'} days_overdue{'}'}{'}'}, {'{'}{'{'} sender_name{'}'}{'}'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Legal Reminder Template */}
        {config.enable_legal_reminder && (
          <Card>
            <CardHeader>
              <CardTitle>Przekazanie do prawnika</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Temat wiadomości
                </label>
                <input
                  type="text"
                  value={config.legal_reminder_subject}
                  onChange={(e) => handleInputChange('legal_reminder_subject', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required={config.enable_legal_reminder}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Treść wiadomości (HTML)
                </label>
                <HTMLEditor
                  value={config.legal_reminder_template}
                  onChange={(value) => handleInputChange('legal_reminder_template', value)}
                  placeholder="Wprowadź treść wezwania prawnego..."
                  height="300px"
                />
                <p className="text-sm text-gray-500 mt-2">
                  Dostępne zmienne: {{invoice_number}}, {{client_name}}, {{amount_gross}}, {{due_date}}, {{days_overdue}}, {{sender_name}}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={saving}
            className="px-6 py-2"
          >
            {saving ? 'Zapisywanie...' : 'Zapisz konfigurację'}
          </Button>
        </div>
      </form>
    </div>
  );
};